import { useMemo } from "react";
import { formatShare } from "#app/widgets/definitions/shared/format-share";
import { z } from "zod";
import {
  Box,
  FUNNEL_BAR_ROW_HEIGHT,
  FunnelBarList,
  Text,
  WidgetEmpty,
  WidgetSkeleton,
  useCategoryRowBudget,
  useWidgetInstance,
  type FunnelBarItem,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { AGE_STATUS_CONFIG } from "#app/lib/age-status";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { resolveStoredTitle } from "../shared/derived-titles";
import { formatDwell } from "../shared/format-dwell";
import { useStageFunnel, type StageFunnelRow, type StageFunnelRows } from "./use-stage-funnel";

/**
 * `stage-funnel` — the stages as bands. One row per stage (or per stage
 * category) in workflow order, on the library's `FunnelBarList`: the band is
 * how many projects sit in the step, the stronger inner band is the Stale
 * share, and the figure columns carry the count, the average time in stage
 * and the overdue count. Done and canceled rows sit under the hairline as the
 * outcomes the run ends in, with their share of everything in scope in
 * place of a dwell. Each row wears its stage's own color; pressing one opens
 * this card's Data drawer on that stage's (or category's) projects.
 *
 * The type key stays `stage-funnel`: it addresses persisted widget rows.
 */
export const stageFunnelConfigSchema = z.looseObject({
  rows: z.enum(["stage", "stageType"]).default("stage"),
  workflow: z.string().nullable().default(null),
  showDwell: z.boolean().default(true),
  showLapsed: z.boolean().default(true),
  filters: widgetFiltersSchema.nullish(),
});

export type StageFunnelConfig = z.infer<typeof stageFunnelConfigSchema>;

/** Title mechanics: the per-rows derived titles are widget-generated and
 *  re-derive when the rows setting flips; renames stick. */
const ROWS_TITLE: Record<StageFunnelRows, string> = {
  stage: "Breakdown by stage",
  stageType: "Breakdown by stage category",
};
/** Every title this family has generated, the "Pipeline" spellings included
 *  (Rob, 2026-09-15 — the card is named for the stages it reads, not for a
 *  sales metaphor). A row still carrying one is the widget's own title, so it
 *  re-derives to the current spelling instead of sticking like a rename. */
const RETIRED_TITLES = ["Pipeline funnel", "Pipeline by stage", "Pipeline by stage category"];
const GENERATED_TITLES = new Set([
  "Stage breakdown",
  ...RETIRED_TITLES,
  ...Object.values(ROWS_TITLE),
]);


function StageFunnelRender({ config }: WidgetRenderContext<StageFunnelConfig>) {
  const { data, loading, error } = useStageFunnel(config.rows, config.workflow, config.filters);
  // Click-through: a row press opens this card's Data drawer on the row's projects.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const emptyTitle = useWindowEmptyTitle("No Projects", config.filters);
  // The list draws every row it is handed, so a short tile budgets rows from
  // its own measured height and names the remainder in a caption.
  const { ref, budget } = useCategoryRowBudget(FUNNEL_BAR_ROW_HEIGHT);

  // The list fits its own columns to the width it measures, so a narrow card
  // needs no span test here: the figure columns drop from the last inward.
  const showLapsed = config.showLapsed;
  const showDwell = config.showDwell;

  const toItem = useMemo(
    () =>
      (row: StageFunnelRow, closed: boolean): FunnelBarItem => {
        const figures = closed
          ? [
              ...(showDwell
                ? [
                    {
                      label: formatShare(row.count, data.total),
                      tooltip: `Share of all Projects in scope`,
                    },
                  ]
                : []),
              ...(showLapsed ? [{ label: "of all" }] : []),
            ]
          : [
              ...(showDwell
                ? [
                    {
                      label: formatDwell(row.avgSeconds),
                      tooltip:
                        row.avgSeconds === null
                          ? "Nothing waiting here"
                          : "Average time the Projects here have waited so far",
                    },
                  ]
                : []),
              // Stale is the figure a manager acts on, so it wears the
              // destructive tone whenever it is non-zero (Rob, 2026-09-08); a
              // clean stage prints its zero in muted ink.
              ...(showLapsed
                ? [
                    {
                      label: `${row.overdue} ${AGE_STATUS_CONFIG.offTrack.label.toLowerCase()}`,
                      tooltip: "Stale follow up; the darker band is their share",
                      ...(row.overdue > 0 ? { tone: "destructive" as const } : {}),
                    },
                  ]
                : []),
            ];
        return {
          id: row.id,
          label: row.label,
          value: row.count,
          partValue: closed ? 0 : row.overdue,
          colorToken: row.color ?? undefined,
          figures,
        };
      },
    [showDwell, showLapsed, data.total],
  );

  const { items, tailItems, hidden } = useMemo(() => {
    const open = data.open.map((row) => toItem(row, false));
    const closed = data.closed.map((row) => toItem(row, true));
    if (budget === null || budget >= open.length + closed.length) {
      return { items: open, tailItems: closed, hidden: 0 };
    }
    const shownOpen = open.slice(0, budget);
    const shownClosed = closed.slice(0, Math.max(0, budget - shownOpen.length));
    return {
      items: shownOpen,
      tailItems: shownClosed,
      hidden: open.length + closed.length - shownOpen.length - shownClosed.length,
    };
  }, [data, toItem, budget]);

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="bars" />;
  }
  if (data.total === 0) {
    return <WidgetEmpty title={emptyTitle} />;
  }

  const handleItemClick =
    drill && instance
      ? (item: FunnelBarItem) => {
          drill.openData(instance.instanceId, {
            kind: "dimension",
            dimension: config.rows,
            ids: [item.id],
            label: item.label,
          });
        }
      : undefined;

  const noun =
    config.rows === "stage"
      ? hidden === 1 ? "stage" : "stages"
      : hidden === 1 ? "category" : "categories";

  return (
    <Box direction="column" fill="flex" gap="2xs">
      <Box ref={ref} fill="flex" minHeight={0}>
        <FunnelBarList
          items={items}
          tailItems={tailItems}
          ariaLabel={ROWS_TITLE[config.rows]}
          onItemClick={handleItemClick}
        />
      </Box>
      {hidden > 0 ? (
        <Box paddingX="sm">
          <Text size="xs" variant="faint" truncate>
            {hidden} more {noun}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

const stageFunnelSettings: WidgetSettingsGroup[] = [
  {
    title: "Source",
    fields: [
      {
        key: "rows",
        kind: "segmented",
        label: "Rows",
        options: [
          { value: "stage", label: "Stages" },
          { value: "stageType", label: "Categories" },
        ],
      },
      { key: "workflow", kind: "workflowSource", label: "Workflow" },
    ],
  },
  {
    title: "Figures",
    fields: [
      { key: "showDwell", kind: "toggle", label: "Time in stage", icon: "clock" },
      { key: "showLapsed", kind: "toggle", label: AGE_STATUS_CONFIG.offTrack.label },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const stageFunnelDef: WidgetModule<StageFunnelConfig> = {
  type: "stage-funnel",
  title: "Stage breakdown",
  dynamicTitle: (config) =>
    resolveStoredTitle(config, (title) => GENERATED_TITLES.has(title), ROWS_TITLE[config.rows]),
  icon: "filter",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 6 },
  configSchema: stageFunnelConfigSchema,
  settings: stageFunnelSettings,
  headerActions: WidgetHeaderFilters,
  // List body: grab the card from anywhere on it — row presses still land
  // because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "stage-funnel.stage",
      label: "Breakdown by stage",
      description:
        "Projects per stage as bands in stage order, with time in stage and the Stale share.",
      section: "stages" satisfies WidgetSection,
      tags: ["projects", "stages", "workflows", "ageHealth"] satisfies readonly WidgetTag[],
      // Label is in the generated family — bake nothing, let `dynamicTitle` track.
      autoTitle: false,
      defaultConfig: { rows: "stage" },
      defaultW: 6,
    },
    {
      key: "stage-funnel.category",
      label: "Breakdown by stage category",
      description:
        "A four-band funnel: not started, in progress, completed and canceled, with time in stage and the Stale share.",
      section: "stages" satisfies WidgetSection,
      tags: ["projects", "stages", "ageHealth"] satisfies readonly WidgetTag[],
      autoTitle: false,
      defaultConfig: { rows: "stageType" },
      defaultW: 6,
    },
  ],
  render: StageFunnelRender,
};
